import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { Controller, FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form'
import { getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION } from '../../common/errorMessages'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../common/integrations'
import { LANGUAGE_OPTIONS } from '../../common/languageUtils'
import {
  createAdditionalObjectsValidatorRegistry,
  type AdditionalObjectsValidator,
  useAdditionalObjectsContactLinking,
} from '../../common/useAdditionalObjectsContactLinking'
import { Button } from '../../components/Button/Button'
import { FormFeedback } from '../../components/Form/FormFeedback'
import { Modal } from '../../components/Modal/Modal'
import { getRoleDisplayName } from '../../components/Roles/utils'
import { SelectDropdown, SelectOption } from '../../components/SelectDropdown/SelectDropdown'
import { AccountIntegrationConfigMappingType, getAccountCRMIntegrationConfig } from '../../models/Account'
import { UserCreateUpdateIntegrations, UserLanguage, UserOwnerType } from '../../models/User'
import { useGetAccountQuery } from '../../store/accountUserSettings/accountUserSettings'
import { selectAccountId } from '../../store/auth/authSlice'
import { useGetExternalUserByIdQuery, useSaveExternalUserByIdMutation, useUpdateExternalUserRolesMutation } from '../../store/externalUser/externalUser'
import { useAppSelector } from '../../store/hooks'
import { useGetIntegrationUsersQuery, useLazyGetIntegrationUsersQuery } from '../../store/integrations/integrations'
import { useGetRolesQuery } from '../../store/role/role'
import { useGetAllUsersQuery, useGetMeQuery } from '../../store/user/user'
import { getContactDropdownComponent } from '../MeetingSummary/ContactDropdownItems'
import { getContactOptions } from '../MeetingSummary/CRM/utils'
import { getUserName } from '../utils'
import { DEFAULT_CONTACT_ROLE_ID } from './AddContactModal'

const INVALID_INTEGRATION_ERROR_MESSAGES = [INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION]

export interface EditContactModalProps {
  userId: string
  show: boolean
  onSave: () => void
  onCancel: () => void
}

type FormValues = {
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  role: string
  ownerId: string
  integrations: UserCreateUpdateIntegrations[]
  language: UserLanguage | null
}

export const EditContactModal: React.FC<EditContactModalProps> = ({ userId, show, onSave, onCancel }) => {
  const accountId = useAppSelector(selectAccountId)

  const defaultValues = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    role: '',
    ownerId: '',
    language: null,
    integrations: [],
  }
  const validatorRegistry = useMemo(() => createAdditionalObjectsValidatorRegistry(), [])
  const { run: runAdditionalObjectsValidators, register: registerAdditionalObjectsValidator } = validatorRegistry
  const [saveUserById, { isLoading: isUpdating, isSuccess }] = useSaveExternalUserByIdMutation()
  const [updateUserRoles, { isLoading: isRolesUpdating, isSuccess: updateRolesSuccess }] = useUpdateExternalUserRolesMutation()

  useEffect(() => {
    if (isSuccess && updateRolesSuccess) {
      onSave()
    }
  }, [isSuccess, updateRolesSuccess, onSave])

  const form = useForm<FormValues>({ defaultValues, mode: 'onChange' })
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = form

  const handleSave = async (data: FormValues) => {
    const { isValid, integrations } = await runAdditionalObjectsValidators(data.integrations)
    if (!isValid) {
      return
    }

    saveUserById({
      accountId,
      userId,
      userData: {
        firstName: data.firstName,
        lastName: data.lastName,
        name: getUserName(data.firstName, data.lastName) || undefined,
        email: data.email,
        //phoneNumber: data.phoneNumber,
        referenceUserId: null,
        owner:
          !data.ownerId || data.ownerId === 'null'
            ? null
            : {
                ownerId: data.ownerId,
                ownerType: UserOwnerType.USER,
              },
        integrations,
        settings: {
          language: data.language || null,
        },
      },
    })
    updateUserRoles({
      accountId,
      userId: userId,
      roleIds: [data.role],
    })
  }

  const body = (
    <Container>
      <Row className='pb-3'>
        <Col>
          <h4 className='d-flex justify-content-center align-items-center'>Edit Contact</h4>
        </Col>
      </Row>
      <Row>
        <FormProvider {...form}>
          <EditContactModalForm userId={userId} registerAdditionalObjectsValidator={registerAdditionalObjectsValidator} />
        </FormProvider>
      </Row>
    </Container>
  )

  return (
    <Modal
      show={show}
      onHide={onCancel}
      header={<></>}
      body={body}
      showFooterActions={true}
      onFooterPrimaryAction={handleSubmit(handleSave)}
      footerPrimaryActionLoading={isUpdating || isRolesUpdating || isSubmitting}
      centered={true}
    />
  )
}

interface EditContactModalFormProps {
  userId: string
  registerAdditionalObjectsValidator: (validator: AdditionalObjectsValidator) => () => void
}

const EditContactModalForm: React.FC<EditContactModalFormProps> = ({ userId, registerAdditionalObjectsValidator }) => {
  const accountId = useAppSelector(selectAccountId)

  const { data: currentUser, isFetching: isFetchingCurrentUser } = useGetMeQuery({ accountId })
  const { data: userData, isFetching: isFetchingUserData } = useGetExternalUserByIdQuery(
    {
      accountId,
      userId,
    },
    { refetchOnMountOrArgChange: true },
  )
  const { data: roleData, isFetching: isFetchingRoleData } = useGetRolesQuery(
    { accountId, pageRequest: { page: 0, size: 500 }, externalOnly: true },
    { refetchOnMountOrArgChange: true },
  )
  const { data: users, isFetching: usersFetching } = useGetAllUsersQuery({
    accountId: accountId,
    identityTypes: ['idp'],
  })

  const userOptions: SelectOption[] = [
    { id: 'null', label: 'No one' },
    ...(users?.data.map((user) => {
      return { id: user.id, label: getUserDisplayName(user) }
    }) ?? []),
  ]

  const { control, setValue, watch } = useFormContext<FormValues>()
  const integrations = watch('integrations')

  useEffect(() => {
    if (!userData && !roleData) {
      return
    }
    if (userData) {
      setValue('firstName', userData.data.firstName ?? '')
      setValue('lastName', userData.data.lastName ?? '')
      setValue('email', userData.data.email ?? '')
      //setValue('phoneNumber', userData.data.phoneNumber ?? '')
      setValue('integrations', userData.data.integrations ?? [])
      if (userData.data.roles.length) {
        setValue('role', userData.data.roles[0].id)
      }

      if (userData.data.owner) {
        setValue('ownerId', userData.data.owner.ownerId)
      }
      if (userData.data.settings?.language) {
        setValue('language', userData.data.settings.language)
      }
    }
    if (roleData) {
      const defaultRole = roleData.data.find((r) => r.id === DEFAULT_CONTACT_ROLE_ID)
      if (defaultRole) {
        setValue('role', defaultRole.id)
      }
    }
  }, [userData, roleData, setValue])

  const roleOptions =
    roleData?.data.map((role) => {
      return { id: role.id, label: getRoleDisplayName(role) }
    }) || []

  const integrationsList =
    currentUser?.integrations
      ?.filter((e) => e.type === 'crm' || e.type === 'planning' || e.type === 'portfolio' || e.type === 'tax' || e.type === 'estate_planning')
      .map(({ serviceId }) => {
        const existingIntegration = integrations.find((e) => e.serviceId === serviceId)
        if (!existingIntegration) {
          return { serviceId }
        } else {
          return { serviceId, externalId: existingIntegration.externalId }
        }
      }) ?? []

  return (
    <Container className='d-flex flex-column gap-4 p-0'>
      {isFetchingUserData || isFetchingRoleData || usersFetching || isFetchingCurrentUser ? (
        <div className='d-flex align-items-center justify-content-center'>
          <Spinner />
        </div>
      ) : (
        <>
          <Row>
            <Col>
              <Controller
                name='email'
                control={control}
                rules={{
                  required: 'Email address is required.',
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: 'Entered value does not match email format',
                  },
                }}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label>Email address*</Form.Label>
                    <Form.Control id={field.name} type='email' placeholder='Enter email address…' {...field} isInvalid={!!fieldState.error} />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Controller
                name='firstName'
                control={control}
                render={({ field }) => (
                  <>
                    <Form.Label>First name</Form.Label>
                    <Form.Control id={field.name} type='text' placeholder='Enter first name…' {...field} />
                  </>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Controller
                name='lastName'
                control={control}
                render={({ field }) => (
                  <>
                    <Form.Label>Last name</Form.Label>
                    <Form.Control id={field.name} type='text' placeholder='Enter last name…' {...field} />
                  </>
                )}
              />
            </Col>
          </Row>
          {/*<Row>*/}
          {/*  <Col>*/}
          {/*    <Controller*/}
          {/*      name='phoneNumber'*/}
          {/*      control={control}*/}
          {/*      rules={{*/}
          {/*        pattern: { value: /^\+[1-9][0-9]{0,24}$/i, message: 'Invalid phone number. E.g. +11234567890' },*/}
          {/*      }}*/}
          {/*      render={({ field, fieldState }) => (*/}
          {/*        <>*/}
          {/*          <Form.Label>Phone number</Form.Label>*/}
          {/*          <Form.Control id={field.name} type='text' placeholder='Enter phone number…' {...field} />*/}
          {/*          {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}*/}
          {/*        </>*/}
          {/*      )}*/}
          {/*    />*/}
          {/*  </Col>*/}
          {/*</Row>*/}
          <Row>
            <Col>
              <Controller
                name='role'
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label>Role</Form.Label>
                    <SelectDropdown
                      className='zocks-px-0.75 py-2'
                      options={roleOptions}
                      initialSelectedOption={roleOptions.find((option) => option.id === field.value)}
                      onOptionSelected={(option) => field.onChange(option.id)}
                    />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Controller
                name='ownerId'
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label>Owner</Form.Label>
                    <SelectDropdown
                      className='zocks-px-0.75 py-2'
                      options={userOptions}
                      initialSelectedOption={userOptions.find((option) => option.id === field.value)}
                      onOptionSelected={(option) => {
                        field.onChange(option.id)
                      }}
                      disabled={usersFetching}
                    />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Controller
                name='language'
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label>Language</Form.Label>
                    <SelectDropdown
                      className='zocks-px-0.75 py-2'
                      options={LANGUAGE_OPTIONS}
                      initialSelectedOption={field.value ? (LANGUAGE_OPTIONS.find((option) => option.id === field.value) ?? null) : null}
                      onOptionSelected={(option) => field.onChange(option.id as UserLanguage)}
                      placeholder='Select Language'
                    />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )}
              />
            </Col>
          </Row>
          {integrationsList.map(({ serviceId, externalId }) => (
            <IntegrationSelectorRow
              key={serviceId}
              serviceId={serviceId}
              externalId={externalId}
              registerAdditionalObjectsValidator={registerAdditionalObjectsValidator}
            />
          ))}
        </>
      )}
    </Container>
  )
}

const CONTACT_MAPPING_LABEL_MAPPING: { [key: string]: string } = {
  contact: 'Contact',
  account: 'Account',
}

interface IntegrationSelectorRowProps {
  serviceId: IntegrationServiceId
  externalId?: string
  registerAdditionalObjectsValidator: (validator: AdditionalObjectsValidator) => () => void
}

const IntegrationSelectorRow: React.FC<IntegrationSelectorRowProps> = ({ serviceId, externalId, registerAdditionalObjectsValidator }) => {
  const accountId = useAppSelector(selectAccountId)

  const {
    data: contacts,
    isFetching: isContactsFetching,
    isError: isContactsError,
    error: contactsError,
  } = useGetIntegrationUsersQuery({ accountId, connector: serviceId })
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const [triggerGetContacts, { isError: isTriggerGetContactsError, error: triggerContactsError }] = useLazyGetIntegrationUsersQuery()

  const { control, watch } = useFormContext<FormValues>()
  const integrations: UserCreateUpdateIntegrations[] = watch('integrations') ?? []
  const {
    append: appendIntegration,
    update: updateIntegration,
    remove: removeIntegration,
  } = useFieldArray({
    control,
    name: 'integrations',
  })

  const [searchContactOptions, setSearchContactOptions] = useState<SelectOption[] | null>(null)
  const [initialSelectedContactOption, setInitialSelectedContactOption] = useState<SelectOption | null>(null)
  const [loading, setLoading] = useState<boolean>(!!externalId)

  const serviceIntegration = getAccountCRMIntegrationConfig(account?.data, serviceId)
  const contactMapping = serviceIntegration?.mappings[AccountIntegrationConfigMappingType.CONTACT] ?? 'contact'
  const contactLabel =
    typeof contactMapping === 'string'
      ? CONTACT_MAPPING_LABEL_MAPPING[contactMapping]
      : (contactMapping.displayName ?? CONTACT_MAPPING_LABEL_MAPPING[contactMapping.objectName] ?? 'Contact')

  const getContactByExternalId = useCallback(
    async (externalId: string) => {
      try {
        const { data } = await triggerGetContacts({
          accountId,
          connector: serviceId,
          filters: { externalIds: [externalId] },
        }).unwrap()
        return data[0]
      } catch (e) {
        console.error(e)
      }
    },
    [triggerGetContacts, accountId, serviceId],
  )

  const initialContactOptions = useMemo(() => {
    if (contacts && contacts.data) {
      return getContactOptions(contacts.data, serviceId)
    }
    return []
  }, [contacts, serviceId])

  useEffect(() => {
    if (!externalId) {
      return
    }
    getContactByExternalId(externalId)
      .then((contact) => {
        if (!contact) {
          return
        }
        const options = getContactOptions([contact], serviceId)
        setInitialSelectedContactOption(options[0] ?? null)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [serviceId, externalId, getContactByExternalId])

  const onSearchInputChange = async (searchInput: string) => {
    try {
      const contactsData = await triggerGetContacts({
        accountId,
        connector: serviceId,
        filters: { name: searchInput },
      }).unwrap()
      if (contactsData && contactsData.data) {
        setSearchContactOptions(getContactOptions(contactsData.data, serviceId))
      }
    } catch (e) {
      console.error(e)
    }
  }

  const { isAdditionalObjectsContactLinking, identifierValue, identifierError, isLoadingIdentifier, handleIdentifierChange } =
    useAdditionalObjectsContactLinking({
      serviceId,
      initialExternalId: externalId,
      registerAdditionalObjectsValidator,
    })

  if (
    (isContactsError && INVALID_INTEGRATION_ERROR_MESSAGES.some((ignoredMessage) => JSON.stringify(contactsError).match(ignoredMessage))) ||
    (isTriggerGetContactsError && INVALID_INTEGRATION_ERROR_MESSAGES.some((ignoredMessage) => JSON.stringify(triggerContactsError).match(ignoredMessage)))
  ) {
    return (
      <>
        <Container className='p-0'>
          <Row>
            <Col>Your integration is invalid, please reconnect your integration:</Col>
          </Row>
        </Container>
      </>
    )
  }

  const handleOnOptionSelected = async (option: SelectOption) => {
    setLoading(true)
    const contact = await getContactByExternalId(option.id)
    if (!contact || !contact.integrations) {
      setLoading(false)
      return
    }
    const [contactIntegration] = contact.integrations
    const userIntegration: UserCreateUpdateIntegrations = {
      id: contactIntegration.id,
      type: contactIntegration.type,
      serviceId: contactIntegration.serviceId,
      externalId: contactIntegration.externalId,
      externalAccountId: contactIntegration.externalAccountId,
    }
    const integrationIndex = integrations.findIndex((e) => e.serviceId === serviceId)
    if (integrationIndex > -1) {
      updateIntegration(integrationIndex, userIntegration)
    } else {
      appendIntegration(userIntegration)
    }
    setLoading(false)
  }

  const handleRemoveButtonClicked = () => {
    const integrationIndex = integrations.findIndex((e) => e.serviceId === serviceId)
    if (integrationIndex > -1) {
      removeIntegration(integrationIndex)
    }
    setInitialSelectedContactOption(null)
    handleIdentifierChange('')
  }

  const serviceIdDisplayName = getIntegrationDisplayName(serviceId)

  return (
    <Row>
      <Col>
        <Form.Label>
          Linked {serviceIdDisplayName} {contactLabel ?? 'Contact'}
        </Form.Label>
        {isAdditionalObjectsContactLinking ? (
          isAccountFetching || loading || isLoadingIdentifier ? (
            <div className='d-flex justify-content-center'>
              <Spinner />
            </div>
          ) : (
            <div className='d-flex align-items-center zocks-gap-0.5'>
              <div className='flex-grow-1'>
                <Form.Control
                  type='text'
                  value={identifierValue}
                  placeholder='Enter external identifier…'
                  onChange={(event) => {
                    handleIdentifierChange(event.target.value)
                  }}
                  isInvalid={!!identifierError}
                />
                {identifierError && <FormFeedback>{identifierError}</FormFeedback>}
              </div>
              <Button variant='primaryGhost' className='zocks-p-0.75' onClick={handleRemoveButtonClicked} disabled={!identifierValue}>
                <i className='bi bi-trash' />
              </Button>
            </div>
          )
        ) : isContactsFetching || isAccountFetching || loading || !contacts ? (
          <div className='d-flex justify-content-center'>
            <Spinner />
          </div>
        ) : (
          <div className='d-flex align-items-center zocks-gap-0.5'>
            <SelectDropdown
              initialSelectedOption={initialSelectedContactOption}
              options={searchContactOptions ?? initialContactOptions}
              className='py-2 zocks-px-0.75'
              onOptionSelected={handleOnOptionSelected}
              DropDownItemOptionComponent={getContactDropdownComponent(serviceId)}
              searchable={true}
              onSearchInputChange={onSearchInputChange}
              placeholder={`Select your ${contactLabel}`}
            />
            <Button variant='primaryGhost' className='zocks-p-0.75' onClick={handleRemoveButtonClicked} disabled={!externalId}>
              <i className='bi bi-trash' />
            </Button>
          </div>
        )}
      </Col>
    </Row>
  )
}
